/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderIpAddrList, renderSiteList } from '../../properties/Properties';
import { useHypervisor, timeRangeFilterId, hypervisorFilterId } from '../../filters';
import { getMsOf } from '../../../utilities/TimeRangeNavigationUtilities';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import { stringProperty, listProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { getUpTimeDuration } from '../../../utilities/DateTimeUtilities';

const LOOPBACK_IP = '127.0.0.1';

const powerStates = {
    poweredOn: 'On',
    poweredOff: 'Off',
};

const propertiesColumns = [
    {
        properties: [
            stringProperty(t`Name`, 'name'),
            stringProperty(t`IP Address`, 'ipAddress'),
            stringProperty(t`Virtual Type`, 'subType', () => t`Hypervisor`),
            stringProperty(t`CPU Info`, 'virtualization.cpuInfo'),
            stringProperty(t`Memory Size`, 'virtualization.memorySize'),
            stringProperty(t`Power State`, 'virtualization.powerState', renderPowerState),
            stringProperty(t`Up Time`, 'virtualization.bootTime', getUpTimeDuration),
            stringProperty(t`Vendor`, 'vendor'),
            stringProperty(t`Technology`, 'virtualization.technology'),
            stringProperty(t`vCenter`, 'virtualization.vCenter'),
            stringProperty(t`Data Center`, 'virtualization.dataCenter'),
            listLinkProperty(t`Sites`, 'sites', [], [hypervisorFilterId], renderSiteList),
        ],
    },
    {
        properties: [listProperty(t`IP Addresses`, 'ipAddresses', renderIpAddrList)],
    },
];

/**
 * Convert the array of property name/value pairs into an object, then convert ipAddresses and loopbackIps from
 * pseudo-JSON strings into string arrays. Annotate any IPs in ipAddresses which are loopbacks, and remove the
 * localhost loopback from the list if present. Finally, include sites in the main set of properties.
 * @param data
 * @param sites
 * @returns {*}
 */
function buildPropertiesData(data, sites) {
    const properties = _(data).keyBy('name').mapValues('value').value();
    const loopbacks = decodeStringList(properties.loopbackIps);
    const ipAddresses = decodeStringList(properties.ipAddresses);
    const ipAddressesNoLoopback = _.pull(ipAddresses, LOOPBACK_IP);
    properties.ipAddresses = _.map(ipAddressesNoLoopback, (ip) => {
        return { ipAddress: ip, loopback: loopbacks.indexOf(ip) >= 0 };
    });
    properties.sites = sites;
    return properties;
}

function decodeStringList(str) {
    const list = str ? str.replace(/[[\]]/g, '') : null;
    if (!list) return [];
    return list.split(/,[ ]*/);
}

function renderPowerState(value) {
    return powerStates[value] || t`Unknown`;
}

/**
 * Combine data from serverinfo and device queries
 * @returns {Object}
 */
function usePropertiesData() {
    const hypervisor = useHypervisor();
    const dataService = useDataService();
    const [props, setProps] = useState();
    const [timeRange] = useCurrentFilterStateById(timeRangeFilterId);
    const hypervisorId = hypervisor?.uuid;
    const start = timeRange?.start;
    const end = timeRange?.end;

    useEffect(() => {
        if (!hypervisorId || !start) return;

        const serverInfoParams = {
            start: getMsOf(start),
            end: getMsOf(end),
            type: 'server',
            server: hypervisorId,
        };
        const serverInfoRequest = dataService.get('/query/serverinfo', serverInfoParams);
        const deviceRequest = dataService.get(`/ngp/v2/devices/${hypervisorId}`);

        Promise.all([serverInfoRequest, deviceRequest]).then((resolved) => {
            const [serverInfo, deviceData] = resolved;
            setProps(buildPropertiesData(serverInfo?.data, deviceData?.sites));
        });
    }, [dataService, hypervisorId, start, end]);

    return props;
}

export function HypervisorProperties({ data }) {
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function HypervisorPropertiesPanel() {
    const properties = usePropertiesData();
    if (!properties) return null;
    return <HypervisorProperties data={properties} />;
}

HypervisorPropertiesPanel.propTypes = panelPropTypes;

HypervisorPropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Hypervisor Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 250,
};

export default Panel({
    filters: {
        required: [timeRangeFilterId, hypervisorFilterId],
        optional: [],
    },
})(HypervisorPropertiesPanel);
